/**
 * Эфиры — расписание розыгрышей платформы.
 *
 * Пока у каждой акции свой отсчёт на своей странице, не видно главного:
 * розыгрыши идут всё время, их проводят разные бренды, и у каждого есть
 * результат, который можно пересчитать. Эфир собирает их в один таймлайн: что
 * идёт сейчас, что будет и что уже состоялось — с сидом и победителями.
 *
 * Появление строк — общий хук useReveal: таймлайн читают, а не смотрят, и
 * при prefers-reduced-motion он не двигается вовсе.
 */
import type { BroadcastEntry } from '@allswipes/client';
import { Badge, Card, Countdown, Empty, Icon, Skeleton, fmtDateTime, num } from '@allswipes/ui';
import { usePlural, useT } from '@allswipes/i18n';
import { useRef } from 'react';
import { Link } from 'react-router-dom';

import { api } from '../api';
import { useReveal } from '../anim';
import { Shell } from '../components/Shell';
import { useResource } from '../hooks';
import { useSession } from '../session';

export function BroadcastScreen() {
  const t = useT();
  const { profile } = useSession();
  const data = useResource(() => api.broadcast({ region: profile?.user.region }), [profile?.user.region]);
  const root = useRef<HTMLDivElement>(null);

  useReveal(root, [data.data], { selector: '.bentry' });

  const empty =
    data.data && !data.data.live.length && !data.data.upcoming.length && !data.data.done.length;

  return (
    <Shell
      wide
      title={t('Эфиры')}
      subtitle={t('розыгрыши платформы: что идёт сейчас, что будет и чем закончилось')}
    >
      {data.loading && <Skeleton count={4} height={88} />}
      {empty && (
        <Empty icon="calendar" title={t('Розыгрышей пока нет')}>
          {t('Здесь появится расписание: мгновенные механики в эфир не попадают — у них нет даты, к которой можно прийти.')}
        </Empty>
      )}

      <div ref={root}>
        {data.data && data.data.live.length > 0 && (
          <Section title={t('В эфире')} note={t('приём кодов закрыт, список билетов заморожен')}>
            {data.data.live.map((e) => (
              <Entry key={e.campaign.id} entry={e} />
            ))}
          </Section>
        )}

        {data.data && data.data.upcoming.length > 0 && (
          <Section title={t('Скоро')} note={t('дата известна заранее и не двигается без причины')}>
            {data.data.upcoming.map((e) => (
              <Entry key={e.campaign.id} entry={e} />
            ))}
          </Section>
        )}

        {data.data && data.data.done.length > 0 && (
          <Section title={t('Состоялись')} note={t('сид опубликован — результат пересчитывается в браузере')}>
            {data.data.done.map((e) => (
              <Entry key={e.campaign.id} entry={e} />
            ))}
          </Section>
        )}
      </div>
    </Shell>
  );
}

function Section({
  title,
  note,
  children,
}: {
  title: string;
  note: string;
  children: React.ReactNode;
}) {
  return (
    <section style={{ marginBottom: 26 }}>
      <div className="sechead">
        <span className="t">{title}</span>
        <span className="n">{note}</span>
      </div>
      <div className="btimeline">{children}</div>
    </section>
  );
}

function Entry({ entry }: { entry: BroadcastEntry }) {
  const t = useT();
  const plural = usePlural();
  const { campaign: c, brand, state } = entry;

  return (
    <Card className="bentry">
      <div className="bdot" data-state={state} aria-hidden />

      <div className="bwhen">
        {state === 'done' ? (
          <span className="mono">{fmtDateTime(entry.draw_at)}</span>
        ) : state === 'live' ? (
          <Badge tone="live" dot>
            {t('идёт')}
          </Badge>
        ) : (
          <Countdown to={entry.draw_at} />
        )}
      </div>

      <div className="bbody" style={{ minWidth: 0 }}>
        <div className="row" style={{ gap: 8, marginBottom: 4 }}>
          <span className="faint" style={{ fontSize: 12 }}>
            {brand?.name ?? '—'}
          </span>
          {c.prizes_verified_at && (
            <span className="vmark" title={t('Призы проверены платформой')}>
              <Icon name="checkCircle" size={12} />
            </span>
          )}
        </div>

        <Link to={`/c/${c.id}`} className="h2" style={{ display: 'block' }}>
          {c.prize_title || c.name}
        </Link>

        <div className="bmeta">
          <span>
            {num(entry.winner_count)} {plural(entry.winner_count, 'приз', 'приза', 'призов')}
          </span>
          <span>
            {num(entry.tickets)} {plural(entry.tickets, 'билет', 'билета', 'билетов')}
          </span>
          <span>
            {num(entry.participants)}{' '}
            {plural(entry.participants, 'участник', 'участника', 'участников')}
          </span>
        </div>

        {state === 'done' && entry.winners && entry.winners.length > 0 && (
          <div className="bwin">
            {entry.winners.map((w) => (
              <span key={w.ticket_number} className="mono">
                {w.ticket_number}
              </span>
            ))}
            {entry.winner_count > entry.winners.length && (
              <span className="faint">
                {t('и ещё')} {num(entry.winner_count - entry.winners.length)}
              </span>
            )}
          </div>
        )}

        {state === 'done' && entry.seed && (
          <div className="bseed">
            <Icon name="hash" size={12} />
            <span className="mono">{entry.seed}</span>
            <span className="faint">{entry.seed_source}</span>
          </div>
        )}

        <div className="brow">
          {state === 'done' ? (
            <>
              <Link to={`/draw/${c.id}`} className="btn out sm">
                {t('Отчёт о розыгрыше')}
              </Link>
              <Link to={`/verify/${c.id}`} className="btn out sm">
                {t('Пересчитать самому')}
              </Link>
            </>
          ) : (
            <Link to={`/c/${c.id}`} className="btn out sm">
              {state === 'live' ? t('Смотреть акцию') : t('Участвовать')}
            </Link>
          )}
        </div>
      </div>
    </Card>
  );
}
